import * as Sentry from "@sentry/react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslate } from "@tolgee/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useVisitByIdQuery,
  useVisitNoteCreateMutation,
  useVisitNoteUpdateMutation,
} from "../../api/generated/graphql";
import { Label } from "@frontend/lyng/typography/label/Label";
import { Button } from "@frontend/lyng/button";
import { Select } from "@frontend/lyng/forms/select/Select";
import { useCareContext } from "../../providers";
import { useCallback, useMemo } from "react";
import { match, P } from "ts-pattern";
import { useSortingOptions } from "@frontend/lyng/utils/hooks/useSortingOptions";
import { Paragraph } from "@frontend/lyng/typography";
import { TextArea } from "@frontend/lyng/forms";

const schema = z.object({
  careRecipientId: z.string().nullable(),
  note: z.string().min(1, "shiftNote.error.noteRequired"),
});

type FormValues = z.infer<typeof schema>;

const ShiftNote = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { id, careRecipientId, noteId } = useParams();
  const [searchParams] = useSearchParams();
  const {
    state: { viewer },
  } = useCareContext();
  const { nameOrderFn } = useSortingOptions(viewer?.tenantSettings);

  // If care recipient id is in the params then the note is coming from the care recipient page which means it cannot be changed
  // but if it's in the search params then it's coming from the shift page which means it can be changed
  const defaultCareRecipientId = match([
    searchParams.get("noteCareRecipientId"),
    careRecipientId,
  ])
    .with([P.any, P.string], ([_, value]) => value)
    .with([P.string, undefined], ([value]) => (value === "null" ? null : value))
    .with([null, undefined], () => null)
    .exhaustive();

  // Fetch home name for placeholder
  const { data: visitData } = useVisitByIdQuery({
    variables: { visitInstanceId: id ?? "" },
    skip: !id,
  });

  const [noteCreateMutation, { loading: createLoading }] =
    useVisitNoteCreateMutation();
  const [noteUpdateMutation, { loading: updateLoading }] =
    useVisitNoteUpdateMutation();

  // Check if any mutation is in progress
  const isLoading = createLoading || updateLoading;

  const homeName = useMemo(() => {
    if (visitData?.visitById?.subject?.__typename !== "CareHome") {
      return undefined;
    }
    return t("shiftNote.everyoneAt", {
      name: visitData.visitById.subject.name,
    });
  }, [visitData, t]);

  const careRecipientOptions = useMemo(() => {
    return (
      visitData?.visitById?.homeStays.map((stay) => ({
        value: stay.careRecipient.id,
        label: nameOrderFn(stay.careRecipient),
      })) ?? []
    );
  }, [visitData, nameOrderFn]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      careRecipientId: defaultCareRecipientId ?? null,
      note: "",
    },
  });

  const noteContents = useMemo(() => {
    if (noteId) {
      return visitData?.visitById?.visitNotes.find((note) => note.id === noteId)
        ?.note;
    }
    return undefined;
  }, [noteId, visitData]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!id || !viewer) {
        return;
      }

      Sentry.setTag("noteButtonClicked", true);

      Sentry.addBreadcrumb({
        category: "ui.click",
        message: "User interacting with shift note",
        data: {
          visitId: id,
          careRecipientId: values.careRecipientId,
        },
      });

      // if carerecipientid came from params we need to use that value, otherwise we need to use the value from the form
      const noteCareRecipientId = careRecipientId
        ? careRecipientId
        : values.careRecipientId;

      if (noteId) {
        Sentry.addBreadcrumb({
          category: "ui.click",
          message: "User updating shift note",
          data: {
            visitId: id,
            careRecipientId: values.careRecipientId,
          },
        });
        await noteUpdateMutation({
          variables: {
            visitInstanceId: id,
            visitNoteId: noteId,
            input: {
              note: values.note,
              careRecipientId: noteCareRecipientId ?? null,
              authorId: viewer.id,
            },
          },
        });
      } else {
        Sentry.addBreadcrumb({
          category: "ui.click",
          message: "User adding shift note",
          data: {
            visitId: id,
            careRecipientId: values.careRecipientId,
          },
        });
        await noteCreateMutation({
          variables: {
            visitInstanceId: id,
            input: {
              note: values.note,
              careRecipientId: noteCareRecipientId ?? null,
              authorId: viewer.id,
            },
          },
        });
      }
      navigate(-1);
    },
    [
      noteId,
      id,
      viewer,
      noteCreateMutation,
      noteUpdateMutation,
      navigate,
      careRecipientId,
    ],
  );

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (!id || !viewer) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="flex flex-col min-h-screen bg-main px-4 pt-6 pb-4">
        {/* Care Recipient */}
        <Label size="m" className="mb-1">
          {t("shiftNote.for")}
        </Label>
        {!careRecipientId ? (
          <Controller
            name="careRecipientId"
            control={control}
            render={({ field }) => (
              <div className="mb-3">
                <Select
                  options={careRecipientOptions}
                  value={
                    careRecipientOptions.find(
                      (cr) => cr.value === field.value,
                    ) || null
                  }
                  isMulti={false}
                  isClearable={true}
                  isDisabled={isLoading}
                  placeholder={homeName || t("shiftNote.selectCareRecipient")}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  onChange={(option) =>
                    field.onChange(option ? option.value : null)
                  }
                  onBlur={field.onBlur}
                  name={field.name}
                  menuPlacement="bottom"
                />
              </div>
            )}
          />
        ) : (
          <div className="flex items-center mb-4">
            <Paragraph size="m" className="text-greyscale-0 mr-2">
              {
                careRecipientOptions.find((cr) => cr.value === careRecipientId)
                  ?.label
              }
            </Paragraph>
          </div>
        )}

        {/* Note */}
        <Label size="m" className="mb-1">
          {t("shiftNote.note")}
        </Label>
        <Controller
          name="note"
          control={control}
          render={() => (
            <TextArea
              control={control}
              name="note"
              defaultValue={noteContents}
              disabled={isLoading}
              placeholder={t("shiftNote.notePlaceholder")}
              errorMessage={errors.note?.message}
            />
          )}
        />
      </div>
      <div className="fixed bottom-0 w-full bg-main z-30">
        <div className="px-6 pt-2 pb-8 bg-greyscale-800 gap-2 flex justify-center">
          <Button
            variant="secondary"
            text={t("cancel")}
            className="flex-1"
            onClick={handleCancel}
            disabled={isLoading}
          />
          <Button
            variant="primary"
            text={isLoading ? t("saving") : t("save")}
            className="flex-1"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading}
          />
        </div>
      </div>
    </>
  );
};

export default ShiftNote;
